import { Popover, Space, Button, Form, DatePicker, message } from "antd";
import React from "react";
import { useFetcher, useRevalidator } from "react-router-dom";
import { useFetcherBusy } from "~/hooks/useRouteActivity";
import { useTranslation } from "react-i18next";
import { INTENTS } from "~/utils";
import * as APITypes from "~/API";

export const TerminateLeaseButton = ({
  isTerminated,
  leaseID,
  leaseVersion,
}: {
  isTerminated: boolean;
  leaseID: string;
  leaseVersion?: number;
}) => {
  const { t } = useTranslation();
  const [formInstance] = Form.useForm();
  const [showTerminatePopUp, setShowTerminatePopUp] = React.useState(false);
  const fetcher = useFetcher({ key: `terminate-lease-${leaseID}` });
  const revalidator = useRevalidator();
  const isSubmitting = useFetcherBusy(fetcher);
  const disableReview = isTerminated || isSubmitting;
  const lastHandledRef = React.useRef<string | null>(null);

  const toggleTerminatePopUp = () => setShowTerminatePopUp(!showTerminatePopUp);
  const handleSubmitTerminate = (formValues: { endDate: string }) => {
    fetcher.submit(
      {
        body: JSON.stringify({
          leaseID,
          endDate: formValues.endDate,
          status: APITypes.LeaseStatusType.Terminating,
          _version: leaseVersion,
        }),
        intent: INTENTS._terminate_lease,
      },
      { method: "post" }
    );
    toggleTerminatePopUp();
  };

  React.useEffect(() => {
    if (fetcher.state !== "idle" || fetcher.data == null) return;
    const key = `${fetcher.state}:${JSON.stringify(fetcher.data)}`;
    if (lastHandledRef.current === key) return;
    lastHandledRef.current = key;

    const data = fetcher.data as {
      success?: boolean;
      error?: string;
      data?: {
        finalRentSettlement?: {
          balanceDue?: number;
          creditAmount?: number;
          request?:
            | { created: true; transactionID: string }
            | { created: false; reason: string };
          credit?:
            | { created: true; transactionID: string }
            | { created: false; reason: string };
        };
      };
    };

    if (data.success === false && data.error) {
      message.error(data.error);
      return;
    }

    const settlement = data.data?.finalRentSettlement;
    if (
      settlement?.request &&
      "created" in settlement.request &&
      settlement.request.created
    ) {
      message.success(
        t(
          "Final_rent_request_created",
          "Lease terminated. A prorated final rent payment request was created.",
        ),
      );
    } else if (
      settlement?.credit &&
      "created" in settlement.credit &&
      settlement.credit.created
    ) {
      message.success(
        t(
          "Final_rent_credit_created",
          "Lease terminated. A rent credit/refund was created for the overpaid period.",
        ),
      );
    } else if (settlement && (settlement.balanceDue ?? 0) <= 0) {
      message.success(
        t(
          "Lease_terminated_final_rent_settled",
          "Lease terminated. Final rent period is already covered.",
        ),
      );
    } else if (data.success !== false) {
      message.success(t("Form_submitted_successfully"));
    }
    revalidator.revalidate();
  }, [fetcher.data, fetcher.state, revalidator, t]);

  return (
    <Popover
      content={
        <>
          <Form
            form={formInstance}
            disabled={disableReview}
            layout="vertical"
            onFinish={handleSubmitTerminate}
          >
            <Form.Item name="endDate" label={t("End_Date")}>
              <DatePicker name="endDate" />
            </Form.Item>
          </Form>
          <Space>
            <Button onClick={() => toggleTerminatePopUp()}>
              {t("Cancel")}
            </Button>
            <Button
              type="primary"
              ghost
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={() => formInstance.submit()}
            >
              {t("Submit")}
            </Button>
          </Space>
        </>
      }
      open={showTerminatePopUp}
    >
      <Button
        disabled={disableReview}
        loading={isSubmitting}
        type="primary"
        danger
        onClick={() => toggleTerminatePopUp()}
      >
        {t("Terminate")}
      </Button>
    </Popover>
  );
};
